import React, { useCallback, useEffect, useRef, useState } from 'react';
import { App, Button, Card, Col, Grid, Modal, Radio, Row, Space, Spin, Tag, Typography } from 'antd';
import { CheckOutlined, CrownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { fetchSubscriptionPlans, createCheckoutSession, createManualSubscription } from '../../api/subscriptionApi';
import { fetchCurrentOrganization, fetchCurrentUser } from '../../api/userApi';
import { showErrorNotification, classifyError, ErrorTypes } from '../../utils/errorHandling';
import { extractPostalCode, normalizeStreetAddress } from '../../utils/addressParser';
import { toFinnishBusinessId } from '../../utils/vatNormalizer';
import { useSubscription } from './useSubscription';
import useHankoAuth from '../../hooks/useHankoAuth';
import useAppTracking from '../../hooks/useAppTracking';
import { getCurrencySymbol, getPlanDisplay, getVisibleSubscriptionPlans } from './planDisplay';
import { appendUpgradeParams, persistUpgradeIntent } from './upgradeContext';
import { buildCheckoutTrackingProperties, buildUpgradeTrackingProperties } from './subscriptionTracking';
import { bookDemoUrl, openBookDemo } from '../../utils/bookDemo';
import '../billing/BillingPlansView.css';
import { TIER_FEATURE_BULLETS, TierFeatureList } from './TierFeatureList';
import { getInvoiceFormUrl, appendInvoiceOrderParams } from '../../utils/hubspotForms';
import { openInvoiceOrder } from '../../utils/invoiceOrder';
import { getTargetPlanName } from './targetPlan';


const { useBreakpoint } = Grid;

// targetPlan (monitoring|pro|intelligence): open with only that plan's card showing, the rest one
// click away. pricingContext tags pricing_viewed/checkout_started so the opener can be measured.
const SubscriptionPlanModal = ({
    open,
    onClose,
    hideFreePlan = false,
    upgradeContext = null,
    targetPlan = null,
    pricingContext = 'app_subscription_modal',
}) => {
    const { t } = useTranslation();
    const breakpoints = useBreakpoint();
    const isMobile = breakpoints.md === false;
    const { Text } = Typography;
    const { notification } = App.useApp();
    const { user } = useHankoAuth();
    const { trackEvent } = useAppTracking();
    const { plan: currentSubscription, loading: subscriptionLoading, refreshSubscription } = useSubscription();
    const [plans, setPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [subscribing, setSubscribing] = useState(null);
    const [paymentMethodByPlan, setPaymentMethodByPlan] = useState({});
    const [invoiceContactUser, setInvoiceContactUser] = useState(null);
    const [invoiceOrganization, setInvoiceOrganization] = useState(null);
    const [showAllPlans, setShowAllPlans] = useState(false);
    const trackedOpenRef = useRef(false);
    const targetPlanName = getTargetPlanName(targetPlan);
    // The tracked target_plan follows the plan this modal offers, not buildUpgradeTrackingProperties' fixed 'pro'.
    const targetPlanTracking = targetPlanName ? { target_plan: targetPlan } : {};

    // Extracted so the empty state's "Retry" button can re-run it.
    const loadPlans = useCallback(async () => {
        setLoadingPlans(true);
        try {
            const plansData = await fetchSubscriptionPlans();
            setPlans(plansData || []);
        } catch (error) {
            console.error('Failed to load subscription plans:', error);
            const errorType = classifyError(error);
            if (errorType !== ErrorTypes.UNAUTHORIZED) {
                showErrorNotification(error, {
                    title: 'common.error',
                    messageKey: 'common.errors.loadBillingData',
                    t
                });
            }
        } finally {
            setLoadingPlans(false);
        }
    }, [t]);

    useEffect(() => {
        if (!open) return;
        persistUpgradeIntent(upgradeContext);
        loadPlans();
        loadInvoicePrefillData();
    }, [open, loadPlans]);

    useEffect(() => {
        if (!open || trackedOpenRef.current) return;
        trackEvent('pricing_viewed', buildUpgradeTrackingProperties(upgradeContext, {
            pricing_context: pricingContext,
            ...targetPlanTracking,
        }));
        if (upgradeContext) {
            trackEvent('pro_lock_clicked', buildUpgradeTrackingProperties(upgradeContext, {
                pro_lock_context: 'app_subscription_modal',
            }));
        }
        trackedOpenRef.current = true;
    }, [open, trackEvent, upgradeContext, pricingContext, targetPlan]);

    useEffect(() => {
        if (!open) {
            trackedOpenRef.current = false;
            setShowAllPlans(false);
        }
    }, [open]);

    const loadInvoicePrefillData = async () => {
        const [userResult, organizationResult] = await Promise.allSettled([
            fetchCurrentUser(),
            fetchCurrentOrganization()
        ]);

        if (userResult.status === 'fulfilled') {
            setInvoiceContactUser(userResult.value);
        }
        if (organizationResult.status === 'fulfilled') {
            setInvoiceOrganization(organizationResult.value);
        }
    };

    const getInvoiceSetupUrl = (plan) => {
        const displayPlan = getPlanDisplay(plan, t);
        const url = appendInvoiceOrderParams(
            appendUpgradeParams(new URL(getInvoiceFormUrl()), upgradeContext),
            { planName: plan.name, billingInterval: displayPlan.billingInterval }
        );
        const resolvedEmail = invoiceContactUser?.email || user?.email || invoiceOrganization?.email || '';
        const businessCode = invoiceOrganization?.businessCode;
        const yTunnus = toFinnishBusinessId(businessCode);
        const rawStreetAddress = invoiceOrganization?.streetAddress;
        const inferredPostalCode = invoiceOrganization?.postalCode || extractPostalCode(rawStreetAddress);
        const normalizedStreetAddress = normalizeStreetAddress(rawStreetAddress) || rawStreetAddress;
        const params = {
            planId: plan.id,
            planName: displayPlan.name,
            billingInterval: displayPlan.billingInterval,
            currency: displayPlan.currency,
            price: displayPlan.price,
            monthlyPrice: displayPlan.monthlyPrice,
            yearlyPrice: displayPlan.yearlyPrice,
            maxUsers: displayPlan.maxUsers,
            email: resolvedEmail,
            contact_email: resolvedEmail,
            firstname: invoiceContactUser?.firstName,
            lastname: invoiceContactUser?.lastName,
            phone: invoiceContactUser?.phoneNumber || invoiceOrganization?.phoneNumber,
            company: invoiceOrganization?.officialName,
            organization_id: invoiceOrganization?.id,
            business_code: businessCode,
            vat: businessCode,
            '0-2/y_tunnus': yTunnus,
            address: normalizedStreetAddress,
            city: invoiceOrganization?.city,
            state: invoiceOrganization?.state,
            country: invoiceOrganization?.country,
            country_code: invoiceOrganization?.countryCode,
            zip: inferredPostalCode,
        };

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                url.searchParams.set(key, String(value));
            }
        });

        return url.toString();
    };

    const getPaymentMethod = (planId) => paymentMethodByPlan[planId] || 'online';

    const handlePaymentMethodChange = (planId, value) => {
        setPaymentMethodByPlan(prev => ({ ...prev, [planId]: value }));
    };

    const handleSubscribe = async (plan) => {
        if (isPlanDisabled(plan)) {
            return;
        }

        const paymentMethod = getPaymentMethod(plan.id);
        persistUpgradeIntent(upgradeContext);
        if (plan.planType === 'PAID' && paymentMethod === 'invoice') {
            trackEvent('checkout_started', buildCheckoutTrackingProperties(plan, upgradeContext, {
                checkout_context: pricingContext,
                checkout_type: 'invoice_request',
                payment_method: 'invoice',
                ...targetPlanTracking,
            }));
            openInvoiceOrder(getInvoiceSetupUrl(plan));
            return;
        }

        setSubscribing(plan.id);
        try {
            if (plan.planType === 'PAID') {
                trackEvent('checkout_started', buildCheckoutTrackingProperties(plan, upgradeContext, {
                    checkout_context: pricingContext,
                    checkout_type: 'stripe_checkout',
                    payment_method: 'online',
                    ...targetPlanTracking,
                }));
                const { url } = await createCheckoutSession(plan.id);
                if (user?.id) {
                    localStorage.setItem(`tendera:plan-modal-shown:${user.id}`, 'true');
                }
                window.location.href = url;
                return;
            }

            await createManualSubscription(plan.id);
            notification.success({
                message: t('pages.settings.billing.subscribeSuccess'),
                description: t('pages.settings.billing.subscribeSuccessDescription'),
            });
            if (user?.id) {
                localStorage.setItem(`tendera:plan-modal-shown:${user.id}`, 'true');
            }
            await refreshSubscription();
            onClose();
        } catch (error) {
            console.error('Failed to subscribe:', error);
            const errorType = classifyError(error);
            if (errorType !== ErrorTypes.UNAUTHORIZED) {
                showErrorNotification(error, {
                    title: 'common.error',
                    messageKey: 'common.errors.subscribe',
                    t
                });
            }
        } finally {
            setSubscribing(null);
        }
    };

    const isCurrentPlan = (plan) => currentSubscription?.plan?.id === plan.id;

    const isPlanDisabled = (plan) => {
        if (isCurrentPlan(plan)) return true;
        if (plan.planType === 'FREE' && currentSubscription?.plan?.planType === 'PAID') {
            return true;
        }
        return false;
    };

    const getPlanTypeColor = (planType) => {
        switch (planType) {
            case 'FREE':
                return 'default';
            case 'TRIAL':
                return 'blue';
            case 'PAID':
                return 'gold';
            default:
                return 'default';
        }
    };

    const isLoading = subscriptionLoading || loadingPlans;
    const visiblePlans = getVisibleSubscriptionPlans(plans, { hideFreePlan });
    const targetPlanOnly = !showAllPlans && targetPlanName
        ? visiblePlans.filter(plan => plan.name === targetPlanName)
        : [];
    // Unknown target name (renamed/hidden plan) falls back to the normal list.
    const shownPlans = targetPlanOnly.length > 0 ? targetPlanOnly : visiblePlans;
    const isSingleCard = hideFreePlan || targetPlanOnly.length > 0;

    const getPlanTypeLabel = (planType) => (
        t(`pages.settings.billing.planTypes.${planType}`, planType)
    );

    return (
        <Modal
            title={t('pages.settings.billing.title', 'Billing & plans')}
            open={open}
            onCancel={onClose}
            footer={null}
            rootClassName={`subscription-plan-modal${isSingleCard ? ' subscription-plan-modal--single' : ''}`}
            width={isMobile ? '100%' : 520}
            style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : undefined}
            styles={isMobile ? { content: { borderRadius: 0, height: '100dvh', overflowY: 'auto' } } : undefined}
        >
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Spin size="large" />
                </div>
            ) : visiblePlans.length === 0 ? (
                // Plans failed to load (or came back empty) — never leave the modal as a bare
                // white box; offer a retry and the demo path instead.
                <div style={{ textAlign: 'center', padding: '2rem 1rem', display: 'grid', gap: 16, justifyItems: 'center' }}>
                    <Text type="secondary">
                        {t('pages.settings.billing.plansUnavailable', 'Plans couldn’t be loaded right now. Please try again in a moment.')}
                    </Text>
                    <Space size={8} wrap>
                        <Button type="primary" onClick={loadPlans}>
                            {t('common.retry', 'Yritä uudelleen')}
                        </Button>
                        <Button onClick={() => openBookDemo('plan_modal_empty')}>
                            {t('tenderView.upgradeBookDemo', 'Book a demo')}
                        </Button>
                    </Space>
                </div>
            ) : (
                <>
                <Row gutter={[16, 16]} className="subscription-plan-cards">
                    {(() => {
                        const colSpan = isSingleCard
                            ? { xs: 24 }
                            : { xs: 24, md: 12 };
                        return shownPlans.map(plan => (
                            <Col {...colSpan} key={plan.id}>
                            {(() => {
                                const displayPlan = getPlanDisplay(plan, t);
                                return (
                            <Card
                                className={`billing-plan-card ${isCurrentPlan(plan) ? 'current-plan' : ''}`}
                                hoverable={!isPlanDisabled(plan)}
                                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                            >
                                <div className="plan-header">
                                    <h3>{displayPlan.name}</h3>
                                    {!hideFreePlan && (
                                        <Tag color={getPlanTypeColor(plan.planType)}>
                                            {getPlanTypeLabel(plan.planType)}
                                        </Tag>
                                    )}
                                </div>

                                <div className="plan-price">
                                    {plan.price > 0 ? (
                                        <>
                                            <span className="price">{displayPlan.monthlyPriceLabel}</span>
                                            <span className="currency">{getCurrencySymbol(displayPlan.currency)}</span>
                                            <span className="period">/
                                                {displayPlan.billingInterval === 'YEARLY' &&  t('pages.settings.billing.month')}
                                                {displayPlan.billingInterval === 'MONTHLY' && t('pages.settings.billing.month')}
                                                *
                                            </span>
                                            {displayPlan.maxUsers ? (
                                                <span className="plan-users">
                                                    {t('pages.settings.billing.inclUsers', {
                                                        count: displayPlan.maxUsers,
                                                        min: 1,
                                                        max: displayPlan.maxUsers,
                                                        defaultValue: `(incl. 1-${displayPlan.maxUsers} users)`
                                                    })}
                                                </span>
                                            ) : null}
                                        </>
                                    ) : (
                                        <span className="price">{t('pages.settings.billing.free')}</span>
                                    )}
                                </div>

                                <div style={{ flex: 1 }}>
                                    <div className="plan-features">
                                        {plan.planType === 'FREE' ? (
                                            <>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.freeFeatures.aiProfile')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.freeFeatures.finnishThresholds')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.freeFeatures.swedishProcurements')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.freeFeatures.internationalData')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.freeFeatures.hilmaPlans')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.freeFeatures.priorInfo')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.freeFeatures.contractAwards')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.freeFeatures.documents', 'Documents and tender attachments')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.freeFeatures.myBids')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.freeFeatures.advancedSearch', 'Advanced search tool for more precise results')}
                                                </div>
                                            </>
                                        ) : plan.planType === 'PAID' && TIER_FEATURE_BULLETS[plan.name] ? (
                                            <TierFeatureList planName={plan.name} />
                                        ) : plan.planType === 'PAID' ? (
                                            <>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.paidFeatures.allFree', 'All FREE features')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.paidFeatures.procurementMonitoring')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.paidFeatures.upcomingProcurements', 'Upcoming procurements from minutes and budgets')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.paidFeatures.officialDecisions', 'Official decisions')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.paidFeatures.projectPlans', 'Project plans, initiatives, and direct procurements in your field')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.paidFeatures.lowValue', 'Low-value procurements from all sources')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.paidFeatures.unlimitedAiProfiles', 'Unlimited AI search profiles')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {t('pages.settings.billing.paidFeatures.aiAssistant', 'AI search assistant')}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {plan.maxMonitoringProfiles} {t('pages.settings.billing.monitoringProfiles')}
                                                </div>
                                                <div className="feature">
                                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                                    {' '}
                                                    {plan.maxUsers} {t('pages.settings.billing.users')}
                                                </div>
                                                {plan.advancedAnalytics && (
                                                    <div className="feature">
                                                        <CheckOutlined style={{ color: '#52c41a' }} />
                                                        {' '}
                                                        {t('pages.settings.billing.advancedAnalytics')}
                                                    </div>
                                                )}
                                                {plan.prioritySupport && (
                                                    <div className="feature">
                                                        <CrownOutlined style={{ color: '#faad14' }} />
                                                        {' '}
                                                        {t('pages.settings.billing.prioritySupport')}
                                                    </div>
                                                )}
                                                {plan.trialDays && (
                                                    <div className="feature">
                                                        <CheckOutlined style={{ color: '#52c41a' }} />
                                                        {' '}
                                                        {plan.trialDays} {t('pages.settings.billing.daysTrial')}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                            {plan.price > 0 && displayPlan.billingInterval === 'YEARLY' ? (
                                                <div className="plan-billed-note">
                                                    {t('pages.settings.billing.billedYearlyNote', {
                                                sum: displayPlan.yearlyPriceLabel,
                                                defaultValue: `(* billed yearly, ${displayPlan.yearlyPriceLabel} ${getCurrencySymbol(displayPlan.currency)} + vat)`
                                                    })}
                                                </div>
                                            ) : null}
                                </div>

                            {plan.planType === 'PAID' && (
                                <div className="payment-method-block">
                                    <Text strong>{t('pages.settings.billing.paymentMethod', 'Payment method')}</Text>
                                    <Radio.Group
                                        className="payment-method-options"
                                        onChange={(e) => handlePaymentMethodChange(plan.id, e.target.value)}
                                        value={getPaymentMethod(plan.id)}
                                        disabled={isPlanDisabled(plan)}
                                    >
                                        <Radio value="online">
                                            {t('pages.settings.billing.payOnline', 'Pay online (card)')}
                                        </Radio>
                                        <Radio value="invoice">
                                            {t('pages.settings.billing.payByInvoice', 'Pay by invoice (company billing)')}
                                        </Radio>
                                    </Radio.Group>
                                    {getPaymentMethod(plan.id) === 'invoice' && (
                                        <div className="invoice-helper">
                                            <Text type="secondary">
                                                {t(
                                                    'pages.settings.billing.invoiceHelpText',
                                                    'For company invoicing (net terms). We will review and activate your subscription after approval.'
                                                )}
                                            </Text>
                                            <Text type="secondary">
                                                {t(
                                                    'pages.settings.billing.invoicePendingInfo',
                                                    'What happens next? We will contact you within 1 business day to finalize billing.'
                                                )}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            )}

                            {isCurrentPlan(plan) ? (
                                <div
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'center',
                                        padding: '8px 16px',
                                        fontSize: '16px',
                                        borderRadius: '8px',
                                        backgroundColor: '#f0f0f0',
                                        color: '#333',
                                        border: 'none',
                                        cursor: 'default',
                                        fontWeight: 500,
                                        lineHeight: '1.6',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    ✓ {t('pages.settings.billing.currentPlan')}
                                </div>
                            ) : null}
                            {isCurrentPlan(plan) && plan.planType === 'FREE' && (
                                <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={() => {
                                            if (user?.id) {
                                                localStorage.setItem(`tendera:plan-modal-shown:${user.id}`, 'true');
                                            }
                                            onClose();
                                        }}
                                        style={{ padding: 0, height: 'auto', fontWeight: 400, color: '#ff670e' }}
                                    >
                                        {t('pages.settings.billing.continueToService', 'Continue to the service')}
                                    </Button>
                                </div>
                            )}
                            {!isCurrentPlan(plan) && (
                                <Button
                                    type="primary"
                                    block
                                    size="large"
                                    onClick={() => handleSubscribe(plan)}
                                    disabled={isPlanDisabled(plan)}
                                    loading={subscribing === plan.id}
                                    style={!isPlanDisabled(plan) && plan.planType === 'PAID' && getPaymentMethod(plan.id) === 'online' ? {
                                        backgroundColor: '#ff670e',
                                        borderColor: '#ff670e'
                                    } : {}}
                                >
                                    {plan.planType === 'PAID'
                                        ? t('pages.settings.billing.proceedToOrder', 'Proceed to order')
                                        : t('pages.settings.billing.subscribe')
                                    }
                                </Button>
                            )}
                            {plan.planType === 'PAID' && (
                                <Button
                                    block
                                    size="large"
                                    type="default"
                                    href={bookDemoUrl('plan_modal')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginTop: '0.75rem' }}
                                >
                                    {t('pages.settings.billing.bookDemo', 'Book a demo')}
                                </Button>
                            )}
                        </Card>
                                );
                            })()}
                    </Col>
                        ));
                    })()}
                </Row>
                {targetPlanOnly.length > 0 && (
                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                        <Button type="link" onClick={() => setShowAllPlans(true)}>
                            {t('pages.settings.billing.showAllPlans', 'Show all plans')}
                        </Button>
                    </div>
                )}
                </>
            )}
        </Modal>
    );
};

export default SubscriptionPlanModal;
